import Hls from "hls.js";
import { useEffect, useRef, useState } from "react";

/**
 * What a listener sees when the host is broadcasting from the studio.
 *
 * ## Why this is not the dock
 *
 * In catalogue mode the room tells the dock which track to play and the dock
 * fetches it from the CDN, which is why a broadcast survives navigating away.
 * In studio mode there is no track to fetch: the audio the host mixed and the
 * picture they composited arrive together as one HLS stream from the ingest
 * server, and it has a picture, which the dock has nowhere to put.
 *
 * So this element owns playback for the duration, and `RoomAudio` stands down -
 * see the note there. Two players fed from the same room would mean hearing the
 * track twice, a second or so apart.
 *
 * ## Muted until asked, but asking is one click
 *
 * Browsers refuse to autoplay audible media, and a video that silently fails to
 * start looks like a broken stream rather than a policy. So it starts muted and
 * playing - which *is* allowed, and which proves the stream is live.
 *
 * Unmuting used to require pressing "Listen live" first, which conflated two
 * different things: joining the room, which is what the listener count and the
 * chat are keyed on, and hearing the video, which is a property of this
 * element. Somebody who lands on a live profile and wants to hear it should be
 * able to press the speaker.
 *
 * ## The stream can be live before it is playable
 *
 * There is a window after the host presses start where the room says stream
 * mode and the ingest server has not cut a segment yet, so the playlist 404s.
 * That is normal and brief, and it is why a network error here retries rather
 * than reporting a failure at somebody who has done nothing wrong.
 */
export function StreamPlayer({ url }: { url: string }) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(1);
  const [waiting, setWaiting] = useState(true);
  /*
   * What went wrong, in the player's own words.
   *
   * This used to be swallowed: a network error retried forever, a media error
   * called `recoverMediaError()` forever, and anything else destroyed the
   * instance in silence. All three produced the same screen - "connecting..."
   * that never finishes - which is indistinguishable from a host who has not
   * started yet, and impossible to diagnose without attaching a debugger to
   * somebody else's browser.
   *
   * hls.js's `details` string is precise and short (`bufferAppendError`,
   * `manifestIncompatibleCodecsError`, `fragParsingError`), so it goes on the
   * screen. A listener does not know what it means; the host reporting the
   * problem can read it out, and that is the point.
   */
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    const video = ref.current;

    if (!video) return;

    setWaiting(true);
    setFailed(null);

    /*
     * hls.js first, native second - and that order is the whole fix.
     *
     * Asking `canPlayType('application/vnd.apple.mpegurl')` first looks right
     * and is a trap: Chromium answers `"maybe"`, which is truthy, and then
     * fails to demux the playlist it was just handed. The symptom is a video
     * element that reports `readyState 4` and a decoded 960x540 frame while
     * sitting at `currentTime 0` forever, with
     * `DEMUXER_ERROR_COULD_NOT_PARSE` hidden in `video.error`.
     *
     * Where native HLS is genuinely the better engine - iOS Safari, which has
     * no MSE at all - `Hls.isSupported()` is false, so the fallback below is
     * reached for exactly the platforms that need it.
     */
    if (!Hls.isSupported()) {
      if (!video.canPlayType("application/vnd.apple.mpegurl")) return undefined;

      video.src = url;

      return () => {
        video.removeAttribute("src");
        video.load();
      };
    }

    const hls = new Hls({
      /*
       * Low-latency mode off, deliberately.
       *
       * MediaMTX publishes LL-HLS - quarter-second parts, and a playlist
       * request the server holds open until the next one is ready. That
       * requires HTTP/2, because on HTTP/1.1 those blocking requests
       * permanently occupy connections out of the six a browser allows per
       * origin and the media parts queue behind them.
       *
       * We cannot have HTTP/2: `mod_http2` does not work under `mpm_prefork`,
       * and `mpm_prefork` is what `mod_php` requires. So the choice is not
       * between low latency and lower latency, it is between a stream that
       * plays and one that spins.
       *
       * hls.js ignores the parts in this mode and uses whole segments, which
       * MediaMTX is cutting at one second - so `liveSyncDurationCount: 3` puts
       * the playhead about three seconds behind the host. For a set with a
       * chat beside it that is well inside normal; Twitch runs several times
       * that.
       *
       * Turn this back on if the platform ever moves to PHP-FPM and HTTP/2.
       */
      lowLatencyMode: false,
      liveSyncDurationCount: 3,
      backBufferLength: 30,
      enableWorker: true,
    });

    /*
     * Bounded retries, and then the truth.
     *
     * The first errors after a host presses start are expected and harmless -
     * the playlist does not exist until the ingest server has cut a segment -
     * so retrying is right. Retrying *forever* is not: it turns a permanent
     * fault into a permanent spinner.
     */
    let networkRetries = 0;
    let mediaRecoveries = 0;

    hls.on(Hls.Events.ERROR, (_event, data) => {
      // Logged whether fatal or not. Non-fatal errors are usually noise, and
      // occasionally they are the only clue - a codec the browser will not
      // append shows up here long before anything else notices.
      console.warn(
        "[stream]",
        data.type,
        data.details,
        data.fatal ? "(fatal)" : "",
      );

      if (!data.fatal) return;

      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          networkRetries += 1;

          if (networkRetries > 20) {
            hls.destroy();
            setFailed(data.details);

            return;
          }

          hls.startLoad();
          break;

        case Hls.ErrorTypes.MEDIA_ERROR:
          mediaRecoveries += 1;

          if (mediaRecoveries > 3) {
            hls.destroy();
            setFailed(data.details);

            return;
          }

          // The second attempt swaps the audio codec, which is the documented
          // escape from an fMP4 whose audio the browser cannot append.
          if (mediaRecoveries === 2) hls.swapAudioCodec();

          hls.recoverMediaError();
          break;

        default:
          hls.destroy();
          setFailed(data.details);
      }
    });

    hls.loadSource(url);
    hls.attachMedia(video);

    return () => hls.destroy();
  }, [url]);

  useEffect(() => {
    const video = ref.current;

    if (!video) return;

    video.muted = muted;
    video.volume = volume;

    void video.play().catch(() => {
      // Autoplay refused even muted, which some configurations do. The waiting
      // state below is what the listener sees, and their next click starts it.
    });
  }, [muted, volume]);

  return (
    <div
      className="relative w-full overflow-hidden rounded-[20px] border border-[#26264e] bg-black"
      data-stream-player
    >
      <video
        ref={ref}
        // `playsInline` or iOS takes the stream fullscreen the moment it plays,
        // which throws the listener out of the page they were reading.
        playsInline
        autoPlay
        muted
        className="block aspect-video w-full"
        data-stream-video
        onPlaying={() => setWaiting(false)}
        onWaiting={() => setWaiting(true)}
      />

      {failed ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/75 px-6 text-center">
          <span className="meta-caps text-hot-text">
            the stream could not be played
          </span>
          <span className="font-mono text-[10px] text-ghost" data-stream-error>
            {failed}
          </span>
        </div>
      ) : (
        waiting && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <span className="meta-caps text-ghost">connecting…</span>
          </div>
        )
      )}

      {/*
        Over the picture rather than under it, because that is where a video's
        controls belong and because the space beside the player is worth more to
        the room than to a volume slider.
      */}
      {!failed && (
        <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 bg-gradient-to-t from-black/80 to-transparent px-4 pb-3 pt-8">
          <button
            type="button"
            onClick={() => setMuted((on) => !on)}
            aria-pressed={!muted}
            aria-label={muted ? "Unmute" : "Mute"}
            data-stream-unmute
            className="shrink-0 rounded-[var(--radius-pill)] bg-ink px-4 py-1.5 text-[13px] font-bold text-app transition-opacity hover:opacity-90"
          >
            {muted ? "Unmute" : "Mute"}
          </button>

          <label className="flex flex-1 items-center gap-2">
            <span className="sr-only">Volume</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={muted ? 0 : volume}
              data-stream-volume
              onChange={(e) => {
                const next = Number(e.target.value);

                setVolume(next);
                // Dragging the slider up is how people unmute, whatever the
                // button says.
                if (next > 0) setMuted(false);
              }}
              className="w-full max-w-[160px] accent-[var(--color-ink)]"
            />
          </label>

          <span className="meta-caps shrink-0 text-white/70">live</span>
        </div>
      )}
    </div>
  );
}
